/**
 * pickerPlacement — where the reaction picker opens (2026-09-09, Miles: "the
 * reaction pop up came behind the chat interface when it's supposed to be
 * user friendly in front of it").
 *
 * The picker is a body portal; the pane it belongs to is a fixed rectangle
 * (docked on the right, floating, or the whole viewport in fullscreen). The
 * first cut centred the card on its trigger and clamped to the VIEWPORT only,
 * so beside a docked pane the card straddled the pane's left edge. This is
 * the one placement rule: above the anchor when it fits, else below; kept
 * INSIDE the pane's bounds when they are known (else the viewport); and when a
 * region is narrower than the card, centred on it and kept on screen.
 */
export interface PickerAnchor {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

export interface PickerSize {
  width: number;
  height: number;
}

export interface PickerRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface PickerPlacement {
  top: number;
  left: number;
  side: 'above' | 'below';
}

/** Space between the anchor and the card. */
export const PICKER_GAP = 8;
/** Breathing room from a region's edge. */
export const PICKER_PAD = 12;

function intersect(a: PickerRect, b: PickerRect | null | undefined): PickerRect | null {
  if (!b) return null;
  const r = {
    left: Math.max(a.left, b.left),
    top: Math.max(a.top, b.top),
    right: Math.min(a.right, b.right),
    bottom: Math.min(a.bottom, b.bottom),
  };
  if (r.right - r.left < 1 || r.bottom - r.top < 1) return null;
  return r;
}

/**
 * Clamp a card edge into [lo, hi]; when the region cannot hold the card,
 * centre the card on the region and keep it inside the viewport instead.
 */
function clampInto(value: number, size: number, lo: number, hi: number, viewLo: number, viewHi: number): number {
  const min = lo + PICKER_PAD;
  const max = hi - size - PICKER_PAD;
  if (max >= min) return Math.max(min, Math.min(value, max));
  const centred = lo + (hi - lo - size) / 2;
  const vMin = viewLo + PICKER_PAD;
  const vMax = viewHi - size - PICKER_PAD;
  if (vMax >= vMin) return Math.max(vMin, Math.min(centred, vMax));
  return Math.max(viewLo, centred);
}

export function placeReactionPicker(
  anchor: PickerAnchor,
  card: PickerSize,
  viewport: PickerSize,
  bounds?: PickerRect | null,
): PickerPlacement {
  const view: PickerRect = { left: 0, top: 0, right: viewport.width, bottom: viewport.height };
  const region = intersect(view, bounds) ?? view;
  const aw = anchor.width ?? 0;
  const ah = anchor.height ?? 0;
  const fitsAbove = anchor.y - PICKER_GAP - card.height >= region.top + PICKER_PAD;
  const side: PickerPlacement['side'] = fitsAbove ? 'above' : 'below';
  const rawTop = fitsAbove ? anchor.y - PICKER_GAP - card.height : anchor.y + ah + PICKER_GAP;
  const rawLeft = anchor.x + aw / 2 - card.width / 2;
  return {
    top: Math.round(clampInto(rawTop, card.height, region.top, region.bottom, view.top, view.bottom)),
    left: Math.round(clampInto(rawLeft, card.width, region.left, region.right, view.left, view.right)),
    side,
  };
}

/**
 * The transcript's side of the pane (lab finding 6, 2026-09-26). At desktop
 * full screen the thread rail is laid out INSIDE the chat container, so a card
 * clamped to the whole container could open over the rail's thread list. A
 * reaction surface belongs to the transcript: when the rail is laid out on the
 * pane's left edge, the region starts at the rail's right edge. A rail with no
 * width (a phone, the docked pane) or one that is not inside the pane changes
 * nothing, and a rail that would leave no room is ignored rather than trusted.
 */
export function transcriptSideBounds(pane: PickerRect | null, rail: PickerRect | null): PickerRect | null {
  if (!pane) return null;
  if (!rail || rail.right - rail.left <= 0 || rail.bottom - rail.top <= 0) return pane;
  const onLeftEdge = rail.left <= pane.left + 1 && rail.right > pane.left && rail.right < pane.right;
  if (!onLeftEdge) return pane;
  const left = Math.max(pane.left, rail.right);
  if (pane.right - left < 160) return pane;
  return { ...pane, left };
}
